import { APICompsSchema, APIOps } from "@/apis/api-config";
import ApiPaths from "@/apis/base-api-paths";

/** 組織メンバー・招待・IG 割当の型（自動生成スキーマから） */
export type OrganizationMembership = APICompsSchema["OrganizationMembershipSchema"];
export type OrganizationMember = APICompsSchema["OrganizationMemberResponse"];
export type OrganizationInvitation = APICompsSchema["OrganizationInvitationResponse"];
export type OrgInstagramAccount = APICompsSchema["OrgInstagramAccountSchema"];
export type OrganizationRole = APICompsSchema["EnumOrganizationRole"];
export type InvitationVerifyResult = APICompsSchema["VerifyInvitationResponse"];

/** Corporate TOP/WORST メディア型（自動生成スキーマから） */
export type CorpTopWorstMediaItem = APICompsSchema["CorpTopWorstMediaItem"];
export type CorpTopWorstMediaResponse = APICompsSchema["CorpTopWorstMediaResponse"];
export type CorpPeriodSummaryResponse = APICompsSchema["CorpPeriodSummaryResponse"];
export type CorpPeriodSummaryItem = APICompsSchema["CorpPeriodSummaryItem"];

/** Corporate Feed/Reel/Story v2 レスポンス型（Firestore版） */
export type CorpFeedV2Item = {
  media_id: string;
  instagram_business_id: string;
  // アカウント情報
  account_name: string;
  display_name: string;
  profile_picture_url?: string;
  // 投稿情報
  captions?: string;
  media_url?: string;
  permalink?: string;
  timestamp?: string;
  // 基本メトリクス
  recent_like_count: number;
  recent_comment_count: number;
  recent_engagement_count: number;
  recent_impression_count: number;
  recent_reach_count: number;
  recent_saved_count: number;
  // 追加メトリクス
  recent_video_view_count: number;
  recent_share_count: number;
  recent_follows_count: number;
  // エンゲージメント率
  recent_engagement_ratio_by_reach: number;
  recent_engagement_ratio_by_follower: number;
  recent_engagement_ratio_by_imp: number;
  // プロフィールアクティビティ
  recent_profile_activity_bio_link_clicked: number;
  recent_profile_activity_call: number;
  recent_profile_activity_direction: number;
  recent_profile_activity_email: number;
  recent_profile_activity_text: number;
  recent_profile_activity_other: number;
  // メタデータ
  recent_insight_updated_at?: string;
  synced_at?: string;
};

export type CorpReelV2Item = {
  media_id: string;
  instagram_business_id: string;
  // アカウント情報
  account_name: string;
  display_name: string;
  profile_picture_url?: string;
  // 投稿情報
  captions?: string;
  media_url?: string;
  permalink?: string;
  timestamp?: string;
  // リール固有メトリクス
  recent_play_count: number;
  recent_avg_watch_time: number;
  recent_video_view_total_time: number;
  // 共通メトリクス
  recent_like_count: number;
  recent_comment_count: number;
  recent_engagement_count: number;
  recent_reach_count: number;
  recent_saved_count: number;
  recent_share_count: number;
  // エンゲージメント率
  recent_engagement_ratio_by_reach: number;
};

export type CorpStoryV2Item = {
  media_id: string;
  instagram_business_id: string;
  // アカウント情報
  account_name: string;
  display_name: string;
  profile_picture_url?: string;
  // 投稿情報
  media_url?: string;
  permalink?: string;
  timestamp?: string;
  // メトリクス
  recent_impression_count: number;
  recent_reach_count: number;
  recent_follows_count: number;
  recent_navigation_count: number;
  recent_profile_activity_count: number;
  recent_profile_visits_count: number;
  recent_shares_count: number;
  recent_total_interactions_count: number;
};

type CorpMediaV2ListResponse<T> = {
  items: T[];
  pagination: {
    next_cursor?: string;
    has_next: boolean;
    page_size: number;
    total_count?: number;
  };
};

export type CorpMediaV2QueryParams = {
  page_size?: number;
  order_by?: string;
  order_direction?: "asc" | "desc";
  instagram_business_id?: string;
  cursor?: string;
};

export default class CorporateApi extends ApiPaths {
  async postCoroporateLogin(): Promise<
    APIOps["login_api_corporate_auth_login_post"]["responses"]["200"]["content"]["application/json"]
  > {
    return (await this.post(this.toCorporateURL("/auth/login"))).data;
  }

  async getCorporateInstagramTableV2(
    queryParams: APIOps["get_firestore_instagram_list_api_corporate_organizations__organization_id__instagram_account_v2_list_get"]["parameters"]["query"],
  ): Promise<
    APIOps["get_firestore_instagram_list_api_corporate_organizations__organization_id__instagram_account_v2_list_get"]["responses"]["200"]["content"]["application/json"]
  > {
    return (await this.get(this.toCorporateOrgURL("/instagram_account/v2/list"), { params: queryParams })).data;
  }

  async getCorporateInstagramSimpleList(): Promise<
    APIOps["get_instagram_table_simple_for_corp_api_corporate_organizations__organization_id__instagram_account_simple_list_get"]["responses"]["200"]["content"]["application/json"]
  > {
    return (await this.get(this.toCorporateOrgURL("/instagram_account/simple/list"))).data;
  }

  async exportCorporateInstagramListCsv(
    queryParams: APIOps["export_firestore_instagram_list_csv_api_corporate_organizations__organization_id__instagram_account_v2_list_export_csv_get"]["parameters"]["query"],
  ): Promise<Blob> {
    return (
      await this.get(this.toCorporateOrgURL("/instagram_account/v2/list/export/csv"), {
        params: queryParams,
        responseType: "blob",
      })
    ).data;
  }

  async getCorporateInstagramInsightList(
    queryParams: APIOps["get_instagram_table_insight_for_corp_api_corporate_organizations__organization_id__instagram_account_insight_list_post"]["parameters"]["query"],
    requestBody: APIOps["get_instagram_table_insight_for_corp_api_corporate_organizations__organization_id__instagram_account_insight_list_post"]["requestBody"]["content"]["application/json"],
  ): Promise<
    APIOps["get_instagram_table_insight_for_corp_api_corporate_organizations__organization_id__instagram_account_insight_list_post"]["responses"]["200"]["content"]["application/json"]
  > {
    return (
      await this.post(this.toCorporateOrgURL("/instagram_account/insight/list"), requestBody, {
        params: queryParams,
      })
    ).data;
  }

  async getCorporateInstagramDetail(
    instagramAccountId: APIOps["get_instagram_table_detail_for_corp_api_corporate_organizations__organization_id__instagram_account__instagram_account_id__detail_get"]["parameters"]["path"]["instagram_account_id"],
  ): Promise<
    APIOps["get_instagram_table_detail_for_corp_api_corporate_organizations__organization_id__instagram_account__instagram_account_id__detail_get"]["responses"]["200"]["content"]["application/json"]
  > {
    return (
      await this.get(this.toCorporateOrgURL(`/instagram_account/${encodeURIComponent(instagramAccountId)}/detail`))
    ).data;
  }

  async postCorporateInstagramUpdateLabel(
    instagramAccountId: APIOps["update_instagram_account_labels_for_corp_api_corporate_organizations__organization_id__instagram_account__instagram_account_id__update_labels_post"]["parameters"]["path"]["instagram_account_id"],
    requestBody: APIOps["update_instagram_account_labels_for_corp_api_corporate_organizations__organization_id__instagram_account__instagram_account_id__update_labels_post"]["requestBody"]["content"]["application/json"],
  ): Promise<
    APIOps["update_instagram_account_labels_for_corp_api_corporate_organizations__organization_id__instagram_account__instagram_account_id__update_labels_post"]["responses"]["200"]["content"]["application/json"]
  > {
    return (
      await this.post(
        this.toCorporateOrgURL(`/instagram_account/${encodeURIComponent(instagramAccountId)}/update/labels`),
        requestBody,
      )
    ).data;
  }

  async getInstagramCredentials(
    instagram_business_id: string,
  ): Promise<
    APIOps["get_corporate_instagram_credentials_api_corporate_organizations__organization_id__instagram_account_v2_credentials__instagram_business_id__get"]["responses"]["200"]["content"]["application/json"]
  > {
    return (
      await this.get(
        this.toCorporateOrgURL(`/instagram_account/v2/credentials/${encodeURIComponent(instagram_business_id)}`),
      )
    ).data;
  }

  async getCorporateFeedListV2(params: {
    start_date: string;
    end_date: string;
    instagram_business_id?: string;
    keyword?: string;
    label_ids?: string;
  }): Promise<{ items: CorpFeedV2Item[] }> {
    return (await this.get(this.toCorporateOrgURL("/feed/v2/list"), { params })).data;
  }

  async getCorporateReelListV2(params: {
    start_date: string;
    end_date: string;
    instagram_business_id?: string;
    keyword?: string;
    label_ids?: string;
  }): Promise<{ items: CorpReelV2Item[] }> {
    return (await this.get(this.toCorporateOrgURL("/reel/v2/list"), { params })).data;
  }

  async getCorporateFeedFirstList(params: {
    start_date: string;
    end_date: string;
    instagram_business_id?: string;
    keyword?: string;
    label_ids?: string;
  }): Promise<{ items: CorpFeedV2Item[] }> {
    return (await this.get(this.toCorporateOrgURL("/feed/first/list"), { params })).data;
  }

  async getCorporateReelFirstList(params: {
    start_date: string;
    end_date: string;
    instagram_business_id?: string;
    keyword?: string;
    label_ids?: string;
  }): Promise<{ items: CorpReelV2Item[] }> {
    return (await this.get(this.toCorporateOrgURL("/reel/first/list"), { params })).data;
  }

  async getCorporateStoryListV2(params: {
    start_date: string;
    end_date: string;
    instagram_business_id?: string;
    label_ids?: string;
  }): Promise<{ items: CorpStoryV2Item[] }> {
    return (await this.get(this.toCorporateOrgURL("/story/v2/list"), { params })).data;
  }

  async getCorporateTopWorstMedia(params: {
    start_date: string;
    end_date: string;
    limit?: number;
    feed_sort_by?: string;
    reel_sort_by?: string;
    label_ids?: string;
  }): Promise<CorpTopWorstMediaResponse> {
    return (await this.get(this.toCorporateOrgURL("/instagram_account/top-worst-media"), { params })).data;
  }

  // ---- レポート ----

  /** 比較レポートの Excel（HINOMEまとめ／投稿／フォロワーの 3 シート） */
  /** 組織月次レポートの中身（店舗ランキング・上位投稿・推移）。Excel と同じ数字 */
  async getOrgMonthlyReport(
    params: APIOps["get_org_monthly_report_api_corporate_organizations__organization_id__report_org_monthly_get"]["parameters"]["query"],
  ): Promise<
    APIOps["get_org_monthly_report_api_corporate_organizations__organization_id__report_org_monthly_get"]["responses"]["200"]["content"]["application/json"]
  > {
    return (await this.get(this.toCorporateOrgURL("/report/org-monthly"), { params })).data;
  }

  /** 組織月次レポートの Excel */
  async downloadOrgMonthlyExcel(
    params: APIOps["download_org_monthly_excel_api_corporate_organizations__organization_id__report_org_monthly_excel_get"]["parameters"]["query"],
  ): Promise<Blob> {
    return (await this.get(this.toCorporateOrgURL("/report/org-monthly/excel"), { params, responseType: "blob" })).data;
  }

  /** 投稿明細の Excel（お客様の集計 Excel と同じ形。アカウントごとに 4 シート） */
  async downloadPostDetailReportExcel(
    params: APIOps["download_post_detail_excel_api_corporate_organizations__organization_id__report_post_detail_excel_get"]["parameters"]["query"],
  ): Promise<Blob> {
    return (await this.get(this.toCorporateOrgURL("/report/post-detail/excel"), { params, responseType: "blob" })).data;
  }

  /** 「反応が多かったリール投稿」: ラベルのアカウントのリールの上位 */
  /** サマリー用: アカウントごとの、選んだ期間の値（合計・期間末の値・投稿数） */
  async getCorporatePeriodSummary(params: {
    start_date: string;
    end_date: string;
    label_ids?: string;
  }): Promise<CorpPeriodSummaryResponse> {
    return (await this.get(this.toCorporateOrgURL("/instagram_account/period-summary"), { params })).data;
  }

  // ---- 所属組織 ----

  async getMyOrganizations(): Promise<OrganizationMembership[]> {
    return (await this.get(this.toCorporateURL("/auth/me/organizations"))).data;
  }

  // ---- メンバー ----

  async getOrganizationMembers(): Promise<OrganizationMember[]> {
    return (await this.get(this.toCorporateOrgURL("/members"))).data;
  }

  async patchOrganizationMemberRole(firebaseUserId: string, role: OrganizationRole): Promise<OrganizationMember> {
    return (await this.patch(this.toCorporateOrgURL(`/members/${encodeURIComponent(firebaseUserId)}/role`), { role }))
      .data;
  }

  /** メンバーをまとめて削除する。1件でも同じ経路を通す */
  async postRemoveOrganizationMembers(firebaseUserIds: string[]): Promise<{ removed: number }> {
    return (await this.post(this.toCorporateOrgURL("/members/remove"), { firebase_user_ids: firebaseUserIds })).data;
  }

  async postTransferOwner(firebaseUserId: string, myNewRole: OrganizationRole): Promise<OrganizationMember[]> {
    return (
      await this.post(this.toCorporateOrgURL("/members/transfer-owner"), {
        firebase_user_id: firebaseUserId,
        my_new_role: myNewRole,
      })
    ).data;
  }

  // ---- 招待 ----

  async getOrganizationInvitations(): Promise<OrganizationInvitation[]> {
    return (await this.get(this.toCorporateOrgURL("/invitations"))).data;
  }

  async postOrganizationInvitation(email: string, role: OrganizationRole): Promise<OrganizationInvitation> {
    return (await this.post(this.toCorporateOrgURL("/invitations"), { email, role })).data;
  }

  async postResendInvitation(invitationId: string): Promise<OrganizationInvitation> {
    return (await this.post(this.toCorporateOrgURL(`/invitations/${encodeURIComponent(invitationId)}/resend`))).data;
  }

  async deleteInvitation(invitationId: string): Promise<{ message: string }> {
    return (await this.delete(this.toCorporateOrgURL(`/invitations/${encodeURIComponent(invitationId)}`))).data;
  }

  /** 招待の検証（認証不要）。トークンは body で送る（アクセスログに残さない） */
  async postVerifyInvitation(token: string): Promise<InvitationVerifyResult> {
    return (await this.post(this.toCorporatePublicURL("/invitations/verify"), { token })).data;
  }

  /** 招待の受諾（要ログイン）。新規ユーザーのときだけ氏名を渡す */
  async postAcceptInvitation(
    token: string,
    profile?: { first_name: string; last_name: string },
  ): Promise<APICompsSchema["AcceptInvitationResponse"]> {
    return (await this.post(this.toCorporateURL("/invitations/accept"), { token, ...(profile || {}) })).data;
  }

  // ---- Instagram アカウントの割り当て ----

  async getOrganizationInstagramAccounts(): Promise<OrgInstagramAccount[]> {
    return (await this.get(this.toCorporateOrgURL("/instagram_accounts"))).data;
  }

  async getOrganizationInstagramCandidates(): Promise<OrgInstagramAccount[]> {
    return (await this.get(this.toCorporateOrgURL("/instagram_accounts/candidates"))).data;
  }

  async postOrganizationInstagramAccounts(instagramBusinessIds: string[]): Promise<OrgInstagramAccount[]> {
    return (
      await this.post(this.toCorporateOrgURL("/instagram_accounts"), {
        instagram_business_ids: instagramBusinessIds,
      })
    ).data;
  }

  /** Instagramアカウントの割り当てをまとめて解除する。1件でも同じ経路を通す */
  async postRemoveOrganizationInstagramAccounts(instagramBusinessIds: string[]): Promise<{ removed: number }> {
    return (
      await this.post(this.toCorporateOrgURL("/instagram_accounts/remove"), {
        instagram_business_ids: instagramBusinessIds,
      })
    ).data;
  }

  async postVerifyImpersonateToken(impersonateToken: string): Promise<{
    firebase_custom_token: string;
    organization_id: string;
    user: APIOps["login_api_corporate_auth_login_post"]["responses"]["200"]["content"]["application/json"];
  }> {
    return (
      await this.post(
        this.toCorporateURL("/auth/verify-impersonate-token"),
        {},
        {
          params: { impersonate_token: impersonateToken },
        },
      )
    ).data;
  }
}
